import { apiServiceWrapper } from '../../../../Middleware/API/apiRequest';
import { getTokensFromStorage } from '../../../../Utils/Auth/AuthToken';
import { ADMIN_ORGANIZATION } from '../../../../constants/apiRoutes';
import {
  getGraphqlCacheResponse,
  setGraphqlCacheResponse,
} from '../../../../Utils/Storage/IndexDB/indexDbWrappers';
/**
 * list Organizations.
 * @returns {Promise} - A promise that resolves with the list of Organizations or rejects with an error.
 * @param props
 */
export const listOrganization = async ({
  limit,
  offset,
  filter,
  sort,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const data = {
    Filters: filter,
    pageNumber: offset === 0 ? 1 : offset,
    pageSize: limit,
    sort,
  };
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_ORGANIZATION}/Filter`,
    method: 'POST',
    body: data,
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
  });
  if (response?.isError) {
    if (isServiceWorker) {
      let cacheResponse = await getGraphqlCacheResponse(
        headers,
        isServiceWorker,
      );
      return cacheResponse;
    }
    return response;
  }
  if (isServiceWorker) {
    await setGraphqlCacheResponse(headers, response, isServiceWorker);
  }
  return {
    Organizations: response.Payload,
    TotalCount: response?.TotalRecordCount,
  };
};

/**
 * list Unapproved (Pending) Organizations.
 * Pending rows have IsApproved = null. We exclude approved (true) and rejected (false)
 * by adding two `neq` filters.
 *
 * @returns {Promise} - A promise that resolves with the list of Organizations or rejects with an error.
 * @param props
 */
export const listUnapprovedOrganizations = async ({
  limit,
  offset,
  filter,
  sort,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  // Remove any existing approved-status filters to avoid duplication/contradictions.
  const filterTemp = (filter ?? []).filter((f) => {
    const col = `${f?.ColumnName ?? ''}`.toLowerCase();
    return col !== 'isapproved' && col !== 'is_approved';
  });

  const data = {
    Filters: [
      ...filterTemp,
      {
        ColumnName: 'IsApproved',
        Op: 'neq',
        ColumnValue: 'false', // ignore rejected
      },
      {
        ColumnName: 'IsApproved',
        Op: 'neq',
        ColumnValue: 'true', // ignore approved
      },
    ],
    pageNumber: offset === 0 ? 1 : offset,
    pageSize: limit,
    sort,
  };

  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_ORGANIZATION}/Requests/Filter`,
    method: 'POST',
    body: data,
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
  });
  if (response?.isError) {
    if (isServiceWorker) {
      let cacheResponse = await getGraphqlCacheResponse(headers, isServiceWorker);
      return cacheResponse;
    }
    return response;
  }
  if (isServiceWorker) {
    await setGraphqlCacheResponse(headers, response, isServiceWorker);
  }

  // Normalize API (PascalCase) payload while keeping the original fields.
  // - We keep the backend fields as-is so the Unapproved grid can show them.
  // - We also add snake_case aliases so shared components (e.g. approve/reject)
  //   can keep working with `organization_id`.
  const normalizedOrganizations = (response?.Payload ?? []).map((rec) => ({
    ...rec,

    // aliases used elsewhere in the UI
    id: rec?.Id,
    organization_id: rec?.Id,
    name: rec?.Name,
    description: rec?.Description,
    email_domain: rec?.Domain,
    active: rec?.Active,
    created_by: rec?.RequestedBy ?? rec?.CreatedBy,
    created_date: rec?.Created_Date,
    modified_by: rec?.ModifiedBy,
    modified_date: rec?.ModifiedDate,

    // Not provided by the requests API
    obj_prefix: rec?.ObjPrefix ?? null,
  }));

  return {
    Organizations: normalizedOrganizations,
    TotalCount: response?.TotalRecordCount,
  };
};

/**
 * Create Organization.
 * @returns {Promise} - A promise that resolves with the creation of Profile Group or rejects with an error.
 * @param data
 */
export const createOrganization = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_ORGANIZATION}`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    body: data,
    accessToken,
  });
  return response;
};

/**
 * Update Organization.
 * @returns {Promise} - A promise that resolves with the update of Profile Group or rejects with an error.
 * @param data
 */
export const updateOrganization = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_ORGANIZATION}`,
    method: 'PUT',
    moduleName,
    isServiceWorker,
    listController,
    body: data,
    accessToken,
  });
  return response;
};

/**
 * delete Organization.
 * @returns {Promise} - A promise that resolves with the update of Profile Group or rejects with an error.
 * @param data
 */
export const deleteOrganization = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_ORGANIZATION}/${data?.organization_id}`,
    method: 'DELETE',
    moduleName,
    isServiceWorker,
    listController,
    body: data,
    accessToken,
  });
  return response;
};

/**
 * get Organization.
 * @returns {Promise} - A promise that resolves with the Organization or rejects with an error.
 * @param data
 */
export const getOrganization = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_ORGANIZATION}/${data?.organization_id}`,
    method: 'GET',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
  });
  return response;
};
/**
 * request Create Organization.
 * @returns {Promise} - A promise that resolves with the approve of Organization or rejects with an error.
 * @param data
 */
export const requestCreateOrganization = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    // New endpoint for organization requests from applications
    url: `${ADMIN_ORGANIZATION}/Requests`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    body: data,
    accessToken,
  });
  return response;
};
/**
 * Approve Organization.
 * @returns {Promise} - A promise that resolves with the approve of Organization or rejects with an error.
 * @param data
 */
export const approveRejectOrganization = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
  approve,
  rejectionReason,
}) => {
  const { accessToken } = getTokensFromStorage();

  const body = approve
    ? { Approval: true }
    : { Approval: false, RejectionReason: rejectionReason ?? '' };

  const response = await apiServiceWrapper({
    url: `${ADMIN_ORGANIZATION}/Requests/${data?.Id}/Approval`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    body,
    accessToken,
  });
  return response;
};
